import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import moment from "moment";
import {
  BsChevronDown,
  BsChevronUp,
  BsChevronLeft,
  BsChevronRight,
  BsPlusCircle,
  BsSearch,
  BsXLg,
} from "react-icons/bs";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Oval } from "react-loader-spinner";
import { DateCalendar } from "@mui/x-date-pickers";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  getSchedules,
  getScheduleDay,
  toggleShowCreateScheduleModal,
  setDateMoment as reduxSetDate,
  getUncompletedDumpYardsCount,
} from "../scheduleSlice";
import { getDeliveries } from "../../Deliveries/deliveriesSlice";
import { getPickups } from "../../Pickups/pickUpsSlice";
import { getDumpYards, refreshDumpYards } from "../../DumpYards/dumpYardsSlice";
import { ScheduleDriverWidget } from "../ScheduleDriverWidget/ScheduleDriverWidget";
import { DeliveryItem } from "../../Deliveries";
import { PickupItem } from "../../Pickups";
import { DumpYardTemplateItem } from "../../DumpYards";
import {
  ScheduleDetailsModal,
  SetVehicleModal,
  OptimizeSettingsModal,
} from "../modals";
import {
  DeliveryStatus,
  DumpYardStatus,
  DumpYardTemplate,
  PickUpStatus,
  Schedule,
  ScheduleItemType,
} from "../../../models";
import { ScheduleViewToggle, MapView, LockDayToggle } from "../components";

import "./Schedules.scss";

export function Schedules() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showSetVehicleModal, setShowSetVehicleModal] = React.useState<
    Schedule | undefined
  >(undefined);
  const [showOptimizeSettingsModal, setShowOptimizeSettingsModal] =
    React.useState<Schedule | undefined>(undefined);

  const schedules = useAppSelector((state) => state.schedules.schedules);
  const deliveries = useAppSelector((state) => state.deliveries.deliveries);
  const pickups = useAppSelector((state) => state.pickups.pickups);
  const dumpYardTemplates = useAppSelector((state) =>
    state.dumpYards.templates.filter((t) => t.isDumpYard)
  );
  const storageTemplates = useAppSelector((state) =>
    state.dumpYards.templates.filter((t) => t.isStorage)
  );
  const quickStopTemplates = useAppSelector((state) =>
    state.dumpYards.templates.filter((t) => t.isQuickStop)
  );
  const createScheduleItem = useAppSelector(
    (state) => state.schedules.createScheduleItem
  );
  const changeScheduleScheduleItemAPI = useAppSelector(
    (state) => state.schedules.changeScheduleScheduleItem
  );
  const uncompletedDumpYards = useAppSelector(
    (state) => state.schedules.uncompletedDumpYards
  );
  const getUncompletedDumpYardsCountAPI = useAppSelector(
    (state) => state.schedules.getUncompletedDumpYardsCountAPI
  );
  const getSchedulesAPI = useAppSelector(
    (state) => state.schedules.getSchedules
  );
  const getScheduleItemsAPI = useAppSelector(
    (state) => state.schedules.getScheduleItems
  );
  const unassignScheduleItemAPI = useAppSelector(
    (state) => state.schedules.unassignScheduleItem
  );
  const createScheduleItemAPI = useAppSelector(
    (state) => state.schedules.createScheduleItem
  );
  const changeScheduleItemOrderAPI = useAppSelector(
    (state) => state.schedules.changeScheduleItemOrder
  );
  const getScheduleAPI = useAppSelector(
    (state) => state.schedules.getScheduleAPI
  );
  const createScheduleAPI = useAppSelector(
    (state) => state.schedules.createSchedule
  );
  const vehicles = useAppSelector((state) => state.vehicles.vehicles);

  const readyToScheduleDeliveries = deliveries.filter(
    (d) => d.status === DeliveryStatus.CREATED
  );
  const readyToSchedulePickups = pickups.filter(
    (p) => p.status === PickUpStatus.CREATED
  );
  const loading =
    getSchedulesAPI.loading ||
    getScheduleItemsAPI.loading ||
    unassignScheduleItemAPI.loading ||
    createScheduleItemAPI.loading ||
    changeScheduleScheduleItemAPI.loading ||
    changeScheduleItemOrderAPI.loading;

  // Resolve the date to show: prefer the ?date=YYYY-MM-DD URL param (so the view
  // survives reloads and back-navigation from an order edit), otherwise fall back
  // to today — or tomorrow after 4pm.
  const resolveInitialDate = (): moment.Moment => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      const parsed = moment(dateParam, "YYYY-MM-DD", true);
      if (parsed.isValid()) return parsed.startOf("day");
    }
    const fallback = moment();
    if (parseInt(moment().format("HH")) > 16) {
      fallback.add(1, "day");
    }
    return fallback.startOf("day");
  };

  const [dateMoment, setDateMoment] =
    React.useState<moment.Moment>(resolveInitialDate);
  const [date, setDate] = React.useState<string>(() =>
    dateMoment.format("dddd, Do MMMM")
  );
  const [collapsedDeliveries, setCollapsedDeliveries] =
    React.useState<boolean>(false);
  const [collapsedPickups, setCollapsedPickups] =
    React.useState<boolean>(false);
  const [collapsedDumpYards, setCollapsedDumpYards] =
    React.useState<boolean>(true);
  const [collapsedStorages, setCollapsedStorages] =
    React.useState<boolean>(true);
  const [collapsedQuickStops, setCollapsedQuickStops] =
    React.useState<boolean>(true);
  // One shared search across the dump yard / storage / quick stop template lists,
  // matching the app's single "Search by name, address or zip" box. Client-side
  // filter over the already-loaded templates — no new API call.
  const [templateSearch, setTemplateSearch] = React.useState<string>("");
  const [viewType, setViewType] = React.useState<string>("schedule");

  // Case-insensitive contains across name / address / zip, mirroring the app.
  const templateQuery = templateSearch.trim().toLowerCase();
  const isSearchingTemplates = templateQuery.length > 0;
  const matchesTemplateSearch = React.useCallback(
    (t: DumpYardTemplate): boolean => {
      if (!templateQuery) return true;
      return (
        (t.name ?? "").toLowerCase().includes(templateQuery) ||
        (t.address ?? "").toLowerCase().includes(templateQuery) ||
        (t.zip ?? "").toLowerCase().includes(templateQuery)
      );
    },
    [templateQuery]
  );
  const filteredDumpYardTemplates = dumpYardTemplates.filter(
    matchesTemplateSearch
  );
  const filteredStorageTemplates = storageTemplates.filter(
    matchesTemplateSearch
  );
  const filteredQuickStopTemplates = quickStopTemplates.filter(
    matchesTemplateSearch
  );
  // While searching, auto-reveal a section if it has matches (ignoring its
  // collapse state) and keep empty sections closed; otherwise honor the collapse.
  const showDumpYardItems = isSearchingTemplates
    ? filteredDumpYardTemplates.length > 0
    : !collapsedDumpYards;
  const showStorageItems = isSearchingTemplates
    ? filteredStorageTemplates.length > 0
    : !collapsedStorages;
  const showQuickStopItems = isSearchingTemplates
    ? filteredQuickStopTemplates.length > 0
    : !collapsedQuickStops;
  const [showDatePicker, setShowDatePicker] = React.useState<boolean>(false);
  const datePickerRef = React.useRef<HTMLDivElement>(null);
  const [showCreateScheduleModal, setShowCreateScheduleModal] = React.useState<
    Schedule | undefined
  >(undefined);

  // Close the date calendar when clicking outside of it
  React.useEffect(() => {
    if (!showDatePicker) return;
    const onClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showDatePicker]);

  // Keep the URL in sync with the viewed date so it survives reloads and
  // back-navigation. Uses replace so paging through dates doesn't stack history
  // entries — "back" from an order edit returns to the schedule, not a prior date.
  React.useEffect(() => {
    const dateString = dateMoment.format("YYYY-MM-DD");
    if (searchParams.get("date") !== dateString) {
      const next = new URLSearchParams(searchParams);
      next.set("date", dateString);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  React.useEffect(() => {
    const dateString = dateMoment.format("YYYY-MM-DD");
    // Keep the store's dateMoment in lockstep with the viewed date so the
    // create-schedule modal (which reads it from the store) always targets the
    // day on screen — not the store's stale default. Runs on mount and on every
    // date change.
    dispatch(reduxSetDate(dateString));
    dispatch(getSchedules(dateString));
    dispatch(getScheduleDay(dateString));
    dispatch(getDeliveries(dateString));
    dispatch(getPickups(dateString));
    dispatch(getDumpYards(dateString));

    const yesterdayDateString = dateMoment
      .clone()
      .add(-1, "day")
      .format("YYYY-MM-DD");
    dispatch(
      getUncompletedDumpYardsCount({
        date: yesterdayDateString,
        status: DumpYardStatus.CANCELED,
      })
    );
  }, [date]);

  React.useEffect(() => {
    if (
      !createScheduleItem.loading &&
      !!createScheduleItem.value &&
      createScheduleItem.value.type === ScheduleItemType.DUMP_YARD
    ) {
      const dateString = dateMoment.format("YYYY-MM-DD");
      dispatch(getDumpYards(dateString));
    }
  }, [createScheduleItem.loading, !!createScheduleItem.value]);

  React.useEffect(() => {
    // Fetch new dump yards and refresh schedules
    if (
      !changeScheduleScheduleItemAPI.loading &&
      !!changeScheduleScheduleItemAPI.value &&
      !!changeScheduleScheduleItemAPI.value.dumpYardId
    ) {
      const dateString = dateMoment.format("YYYY-MM-DD");

      dispatch(getSchedules(dateString));
      dispatch(refreshDumpYards(dateString));
    }
  }, [
    changeScheduleScheduleItemAPI.loading,
    !!changeScheduleScheduleItemAPI.value,
  ]);

  React.useEffect(() => {
    // Fetch new dump yards
    if (
      !getScheduleAPI.loading &&
      !!getScheduleAPI.value &&
      !getScheduleAPI.value.isOptimizing
    ) {
      const dateString = dateMoment.format("YYYY-MM-DD");

      dispatch(refreshDumpYards(dateString));
    }
  }, [getScheduleAPI.loading, !!getScheduleAPI.value]);

  React.useEffect(() => {
    // After a schedule is created, refetch the viewed day from the server instead
    // of trusting an optimistic local insert. If it was created for another day it
    // correctly won't appear here, so admins immediately see the true state.
    if (!createScheduleAPI.loading && !!createScheduleAPI.value) {
      dispatch(getSchedules(dateMoment.format("YYYY-MM-DD")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createScheduleAPI.loading, !!createScheduleAPI.value]);

  const applyDate = (newMoment: moment.Moment) => {
    setDateMoment(newMoment);
    setDate(newMoment.format("dddd, Do MMMM"));
    dispatch(reduxSetDate(newMoment.format("YYYY-MM-DD")));
  };

  const onDayBeforeClick = () => {
    applyDate(dateMoment.clone().add(-1, "day"));
  };

  const onDayAfterClick = () => {
    applyDate(dateMoment.clone().add(1, "day"));
  };

  const onDateSelect = (newMoment: moment.Moment | null) => {
    if (!newMoment) return;
    applyDate(newMoment.clone().startOf("day"));
    setShowDatePicker(false);
  };

  const onCreateSchedule = () => {
    dispatch(toggleShowCreateScheduleModal());
  };

  const onSetVehicle = (schedule: Schedule) => {
    setShowSetVehicleModal(schedule);
    // TODO: Open your set vehicle modal here
    console.log("Opening set vehicle modal for schedule:", schedule.id);
  };

  const scheduledDeliveriesCount = deliveries.filter(
    (d) =>
      d.status === DeliveryStatus.SCHEDULED ||
      d.status === DeliveryStatus.COMPLETE
  ).length;
  const scheduledPickupsCount = pickups.filter(
    (p) =>
      p.status === PickUpStatus.SCHEDULED || p.status === PickUpStatus.COMPLETE
  ).length;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="schedules-component">
        <div className="header-container">
          <div className="date-container">
            <div className="icon-container" onClick={onDayBeforeClick}>
              <BsChevronLeft className="icon" />
            </div>
            <div className="icon-container" onClick={onDayAfterClick}>
              <BsChevronRight className="icon" />
            </div>

            <div className="date-picker-anchor" ref={datePickerRef}>
              <div
                className="date-text"
                onClick={() => setShowDatePicker((prev) => !prev)}
              >
                {date}
              </div>
              {showDatePicker && (
                <div className="date-calendar-popover">
                  <DateCalendar
                    value={dateMoment}
                    onChange={onDateSelect}
                  />
                </div>
              )}
            </div>
          </div>

          <LockDayToggle date={dateMoment.format("YYYY-MM-DD")} />

          <ScheduleViewToggle selected={viewType} onChange={setViewType} />

          <div
            className="fleet-optimization-button"
            onClick={() =>
              navigate(
                `/fleet-optimization?date=${dateMoment.format("YYYY-MM-DD")}`
              )
            }
          >
            Fleet Optimization
          </div>

          <div className="add-button" onClick={onCreateSchedule}>
            <BsPlusCircle className="icon" />
            Create Schedule
          </div>
        </div>

        <div className="content-container">
          {viewType === "schedule" ? (
            <>
              <div className="schedules-container">
                <div className="title-container">
                  <div className="row">
                    <div className="title">Schedules</div>
                    <div className="title-count">{schedules.length}</div>

                    <div style={{ marginRight: 8, marginLeft: 8 }}>
                      Deliveries
                    </div>
                    <div className="title-count">
                      {scheduledDeliveriesCount}
                    </div>

                    <div style={{ marginRight: 8, marginLeft: 8 }}>Pickups</div>
                    <div className="title-count">{scheduledPickupsCount}</div>

                    {loading && (
                      <div style={{ marginLeft: 8 }}>
                        <Oval
                          color="#131313"
                          height={20}
                          width={20}
                          secondaryColor="#FBD20A"
                        />
                      </div>
                    )}
                  </div>

                  <div className="row">
                    <div style={{ marginRight: 8 }}>Uncompleted dump yards</div>
                    {getUncompletedDumpYardsCountAPI.loading ? (
                      <div style={{ marginLeft: 8 }}>
                        <Oval
                          color="#131313"
                          height={20}
                          width={20}
                          secondaryColor="#FBD20A"
                        />
                      </div>
                    ) : (
                      <div className="title-count">{uncompletedDumpYards}</div>
                    )}
                  </div>
                </div>
                <div className="schedule-items-container">
                  {schedules.map((s) => (
                    <ScheduleDriverWidget
                      schedule={s}
                      key={s.id}
                      onMapPress={() => {
                        setShowCreateScheduleModal(s);
                      }}
                      onSetVehicle={onSetVehicle}
                      onSettingsClick={(schedule) =>
                        setShowOptimizeSettingsModal(schedule)
                      }
                    />
                  ))}
                </div>
              </div>

              <div className="order-items-container">
                <div className="deliveries-container">
                  <div className="heading-bar">
                    <div className="title">
                      Deliveries{" "}
                      <span className="title-count">
                        {readyToScheduleDeliveries.length}
                      </span>
                    </div>
                    <div
                      className="items-collapse"
                      onClick={() =>
                        setCollapsedDeliveries(
                          collapsedDeliveries ? false : true
                        )
                      }
                    >
                      {collapsedDeliveries ? (
                        <BsChevronUp className="small-collapse-icon" />
                      ) : (
                        <BsChevronDown className="small-collapse-icon" />
                      )}
                    </div>
                  </div>
                  {!collapsedDeliveries && (
                    <div className="delivery-items-container">
                      {readyToScheduleDeliveries.map((d) => (
                        <DeliveryItem delivery={d} key={d.id} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="pick-ups-container">
                  <div className="heading-bar">
                    <div className="title">
                      Pick Ups{" "}
                      <span className="title-count">
                        {readyToSchedulePickups.length}
                      </span>
                    </div>
                    <div
                      className="items-collapse"
                      onClick={() =>
                        setCollapsedPickups(collapsedPickups ? false : true)
                      }
                    >
                      {collapsedPickups ? (
                        <BsChevronUp className="small-collapse-icon" />
                      ) : (
                        <BsChevronDown className="small-collapse-icon" />
                      )}
                    </div>
                  </div>
                  {!collapsedPickups && (
                    <div className="pickup-items-container">
                      {readyToSchedulePickups.map((p) => (
                        <PickupItem pickup={p} key={p.id} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="template-search">
                  <BsSearch className="template-search-icon" />
                  <input
                    className="template-search-input"
                    type="text"
                    placeholder="Search by name, address or zip"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                  />
                  {isSearchingTemplates && (
                    <BsXLg
                      className="template-search-clear"
                      onClick={() => setTemplateSearch("")}
                    />
                  )}
                </div>

                <div className="dump-yards-container">
                  <div className="heading-bar">
                    <div className="title">
                      Dump Yards{" "}
                      <span className="title-count">
                        {isSearchingTemplates
                          ? `${filteredDumpYardTemplates.length}/${dumpYardTemplates.length}`
                          : dumpYardTemplates.length}
                      </span>
                    </div>
                    <div
                      className="items-collapse"
                      onClick={() =>
                        setCollapsedDumpYards(collapsedDumpYards ? false : true)
                      }
                    >
                      {collapsedDumpYards ? (
                        <BsChevronUp className="small-collapse-icon" />
                      ) : (
                        <BsChevronDown className="small-collapse-icon" />
                      )}
                    </div>
                  </div>
                  {showDumpYardItems && (
                    <div className="dump-yard-items-container">
                      {filteredDumpYardTemplates.map((t) => (
                        <DumpYardTemplateItem template={t} key={t.id} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="storages-container">
                  <div className="heading-bar">
                    <div className="title">
                      Storages{" "}
                      <span className="title-count">
                        {isSearchingTemplates
                          ? `${filteredStorageTemplates.length}/${storageTemplates.length}`
                          : storageTemplates.length}
                      </span>
                    </div>
                    <div
                      className="items-collapse"
                      onClick={() =>
                        setCollapsedStorages(collapsedStorages ? false : true)
                      }
                    >
                      {collapsedStorages ? (
                        <BsChevronUp className="small-collapse-icon" />
                      ) : (
                        <BsChevronDown className="small-collapse-icon" />
                      )}
                    </div>
                  </div>
                  {showStorageItems && (
                    <div className="dump-yard-items-container">
                      {filteredStorageTemplates.map((t) => (
                        <DumpYardTemplateItem template={t} key={t.id} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="quick-stops-container">
                  <div className="heading-bar">
                    <div className="title">
                      Quick Stops{" "}
                      <span className="title-count">
                        {isSearchingTemplates
                          ? `${filteredQuickStopTemplates.length}/${quickStopTemplates.length}`
                          : quickStopTemplates.length}
                      </span>
                    </div>
                    <div
                      className="items-collapse"
                      onClick={() =>
                        setCollapsedQuickStops(
                          collapsedQuickStops ? false : true
                        )
                      }
                    >
                      {collapsedQuickStops ? (
                        <BsChevronUp className="small-collapse-icon" />
                      ) : (
                        <BsChevronDown className="small-collapse-icon" />
                      )}
                    </div>
                  </div>
                  {showQuickStopItems && (
                    <div className="dump-yard-items-container">
                      {filteredQuickStopTemplates.map((t) => (
                        <DumpYardTemplateItem template={t} key={t.id} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <MapView />
          )}
        </div>
      </div>

      <ScheduleDetailsModal
        isOpen={!!showCreateScheduleModal}
        onClose={() => setShowCreateScheduleModal(undefined)}
        onSetVehicle={() => undefined}
        schedule={showCreateScheduleModal}
      />
      <SetVehicleModal
        isOpen={!!showSetVehicleModal}
        onClose={() => setShowSetVehicleModal(undefined)}
        schedule={showSetVehicleModal}
      />
      <OptimizeSettingsModal
        isOpen={!!showOptimizeSettingsModal}
        onClose={() => setShowOptimizeSettingsModal(undefined)}
        schedule={showOptimizeSettingsModal}
      />
    </DndProvider>
  );
}
